import { format } from 'date-fns';
import { parsePhoneNumber } from 'libphonenumber-js';
import { formatAddress } from 'localized-address-format';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import CountryList from '@swf/components/Inputs/StarsDropDown';
import PhoneNumberInput from '@swf/components/PhoneNumberInput/PhoneNumberInput';
import { Country } from '@swf/core/enums';
import useWizardContext from '@swf/core/hooks/useWizardContext';
import { select } from '@swf/core/store';
import type { WizardContext } from '@swf/core/types/Wizard';
import countries from '@swf/generated/countries.json';

import Address from '../../../../Address/Address';
import City from '../../../../City/City';
import DOB from '../../../../DOB/DOB';
import DialingCode from '../../../../DialingCode/DialingCode';
import PostalCode from '../../../../PostalCode/PostalCode';
import FamilyName from '../../RMFLegalName/components/FamilyName/FamilyName';
import GivenName from '../../RMFLegalName/components/GivenName/GivenName';
import MiddleName from '../../RMFLegalName/components/MiddleName/MiddleName';
import SummaryBox from '../SummaryBox/SummaryBox';

import {
  TXTCLI_Country,
  TXTCLI_Date_of_Birth,
  TXTCLI_Full_Name,
  TXTWCL_Your_address,
  TXTWCL__Phone_Number
} from './DataSummary.messages.json';
import {
  countryList,
  dropdownLabel,
  itemBox,
  itemsWrapper,
  phoneNumberBox
} from './DataSummary.scss';

const CountriesList = [Country.Guernsey, Country.Jersey, Country.GreatBritain, Country.Gibraltar];

const countryOptions = (countries as { value: Country; text: string }[]).filter(({ value }) =>
  CountriesList.includes(value)
);

const DataSummary = () => {
  const [editFullName, setEditFullName] = useState(false);
  const [editDateOfBirth, setEditDateOfBirth] = useState(false);
  const [editAddress, setEditAddress] = useState(false);
  const [editPhoneNumber, setEditPhoneNumber] = useState(false);
  const savedDob = useSelector(select.account.getDateOfBirth);
  const {
    values: { firstName, lastName, dob, country, streetAddress, city, zip, phoneNumber },
    data: { cashierFlow }
  }: WizardContext = useWizardContext();
  const formattedNumber = parsePhoneNumber(phoneNumber, country);

  const formattedAddress = formatAddress({
    postalCountry: country,
    locality: city,
    postalCode: zip,
    addressLines: [streetAddress]
  }).join('\n');

  const formattedDateOfBirth = format(!cashierFlow ? dob : savedDob, 'MM/dd/yyyy');

  return (
    <div className={itemsWrapper}>
      <div className={itemBox}>
        {editFullName ? (
          <>
            <GivenName autoFocus={false} />
            <MiddleName />
            <FamilyName />
          </>
        ) : (
          <SummaryBox
            title={TXTCLI_Full_Name}
            details={`${firstName}  ${lastName}`}
            onClick={() => setEditFullName(true)}
          />
        )}
      </div>
      {!savedDob && (
        <div className={itemBox}>
          {editDateOfBirth ? (
            <DOB />
          ) : (
            <SummaryBox
              title={TXTCLI_Date_of_Birth}
              details={formattedDateOfBirth}
              onClick={() => setEditDateOfBirth(true)}
            />
          )}
        </div>
      )}
      <div className={itemBox}>
        {editAddress ? (
          <>
            <CountryList
              id="country"
              label={TXTCLI_Country}
              options={countryOptions}
              labelClassName={dropdownLabel}
              className={countryList}
            />
            <Address country={country} fieldName="streetAddress" />
            <City fieldName="city" />
            <PostalCode country={country} fieldName="zip" />
          </>
        ) : (
          <SummaryBox
            title={TXTWCL_Your_address}
            details={formattedAddress}
            onClick={() => setEditAddress(true)}
          />
        )}
      </div>
      <div className={itemBox}>
        {editPhoneNumber ? (
          <div className={phoneNumberBox}>
            <DialingCode fieldName="dialingCode" />
            <PhoneNumberInput phoneFormatCountry={country} />
          </div>
        ) : (
          <SummaryBox
            title={TXTWCL__Phone_Number}
            details={formattedNumber.number}
            onClick={() => setEditPhoneNumber(true)}
          />
        )}
      </div>
    </div>
  );
};

export default DataSummary;
